import React from "react";
import {
  Card,
  Image,
  Flex,
  Button,
  Grid,
} from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { DataStore } from "aws-amplify";
import { Product } from "../models";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useState } from "react";
import { Amplify } from "aws-amplify";
import awsmobile from "../aws-exports";
import "./Home.css";
import './ProductList.css'

Amplify.configure({
  ...awsmobile,
  aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
});

const ItemButton = styled(Paper)(({ theme }) => ({
  textAlign: "center",
  backgroundColor: "inherit",
}));

function ProductList(props) {
  // Ensure DataStore is synced
  const startSync = async () => {
    try {
      await DataStore.start();
      console.log('DataStore started and syncing');
    } catch (error) {
      console.error('Error starting DataStore:', error);
    }
  };

  startSync();

  // const { tokens } = useTheme();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  
  function handleAddProduct() {
    navigate("/addproduct");
  }

  function handleEditProduct(currItemId) {
    navigate("/editproduct", { state: { itemId: currItemId } });
  }

  const handleOpenDialog = (productId) => {
    setSelectedProductId(productId);
    setOpen(true);
  };
  
  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedProductId(null);
  };

  const handleDeleteProduct = async () => {
    try {
      const productToDelete = await DataStore.query(Product, selectedProductId);
      if (productToDelete) {
        await DataStore.delete(productToDelete);
        // console.log("Product deleted successfully:", productToDelete);
        navigate("/delete-product-confirmation");
      } else {
        // console.log("Product not found with ID:", selectedProductId);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setOpen(false); // Close the dialog
    }
  };

  // function handleProductDetails(currItemId) {
  //   navigate('/productdetails', { state: { itemId: currItemId } })
  // }

  return (
          
    <>

      <Grid container spacing="2rem" direction="column">
        {props.products.map((item, index) => (
          <Card
            key={index}
            borderRadius="medium"
            variation="elevated"
            style={{
              padding: "1.5rem",
              margin: "1rem 0",
              border: "1px solid #ccc",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "stretch", // Ensure full height alignment
            }}
          >
            <Flex direction="row" gap="2rem" style={{ flex: 1 }}>
              {/* Image Section */}
              <Flex
                justifyContent="center"
                alignItems="center"
                style={{
                  flex: "0 0 250px", // Fix the width of the image section
                  height: "100%", // Full height of the card
                }}
              >
                <Image
                  maxWidth="200px"
                  maxHeight="400px"
                  height="auto"
                  objectFit="contain"
                  src={props.images[item.product_image]}
                  alt={item.product_name}
                />
              </Flex>

              <Flex direction="column" gap="1rem" style={{ flex: 1 }}>

              {/* Content Section */}
              <Flex
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                gap="1.5rem"
                style={{ flex: 1 }}
              >
                {/* Top Section: Brand Name, Product Name, Description, and Icons */}
                <Flex
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  gap="0.5rem"
                >
                  {/* Brand Name */}
                  <Flex justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                    <h3 style={{ margin: 0, fontSize: "1.25rem", textAlign: "left" }}>{item.brand_name}</h3>
                    {/* Icons */}
                    <Flex gap="1rem" alignItems="center">
                      <FaEdit
                        onClick={() => handleEditProduct(item.id)}
                        style={{ fontSize: "1.2em", cursor: "pointer" }}
                      />
                      <MdDelete
                        onClick={() => handleOpenDialog(item.id)}
                        style={{ fontSize: "1.2em", cursor: "pointer" }}
                      />
                    </Flex>
                  </Flex>

                  {/* Product Name and Year */}
                  <p
                    style={{
                      margin: 0,
                      fontWeight: "bold",
                      fontSize: "1rem",
                      textAlign: "left",
                    }}
                  >
                    {item.product_name} ({item.year_of_production})
                  </p>

                  {/* Description */}
                  {item.techSheetDescription && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.875rem",
                        textAlign: "left",
                      }}
                    >
                      {item.techSheetDescription}
                    </p>
                  )}
                </Flex>

                {/* Middle Section: GENERAL INFORMATION and SENSORY CHARACTERISTICS */}
                <Flex direction="row" gap="2rem">
                  {/* GENERAL INFORMATION Section */}
                  <Flex
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    gap="0.75rem"
                    style={{ flex: 1 }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        textDecoration: "underline",
                        textAlign: "left",
                      }}
                    >
                      GENERAL INFORMATION
                    </h5>
                    {item.techSheetProductType && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Product Type:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetProductType}</p>
                      </div>
                    )}
                    {item.techSheetSoil && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Soil:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetSoil}</p>
                      </div>
                    )}
                    {item.techSheetProductionArea && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Production Area:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetProductionArea}</p>
                      </div>
                    )}
                    {item.techSheetFormat && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Format:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetFormat}</p>
                      </div>
                    )}
                    {item.techSheetAgingCapacity && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Aging Capacity:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetAgingCapacity}</p>
                      </div>
                    )}
                    {item.techSheetYearOfPlanting && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Year of Planting:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetYearOfPlanting}</p>
                      </div>
                    )}
                    {item.techSheetVinification && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Vinification:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetVinification}</p>
                      </div>
                    )}
                  </Flex>

                  {/* SENSORY CHARACTERISTICS Section */}
                  <Flex
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    gap="0.75rem"
                    style={{ flex: 1 }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        textDecoration: "underline",
                        textAlign: "left",
                      }}
                    >
                      SENSORY CHARACTERISTICS
                    </h5>
                    {item.techSheetAppearance && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Appearance:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetAppearance}</p>
                      </div>
                    )}
                    {item.techSheetNose && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Nose:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetNose}</p>
                      </div>
                    )}
                    {item.techSheetPalate && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Palate:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetPalate}</p>
                      </div>
                    )}
                    {item.techSheetPairings && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Pairings:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetPairings.join(" | ")}</p>
                      </div>
                    )}
                    {item.techSheetServingTemperatureMin && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Serving Temp:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                          {item.techSheetServingTemperatureMin} - {item.techSheetServingTemperatureMax} °C
                        </p>
                      </div>
                    )}
                    {item.techSheetCertifications && (
                      <div style={{ textAlign: "left" }}>
                        <strong style={{ fontSize: "0.875rem" }}>Certifications:</strong>
                        <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{item.techSheetCertifications.join(" | ")}</p>
                      </div>
                    )}
                  </Flex>
                </Flex>
              </Flex>


              {/* Additional Information Row */}
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ width: "100%", marginTop: "1rem" }}
              >
                {item.price_ex_vat && (
                  <div style={{ textAlign: "left" }}>
                    <strong style={{ fontSize: "0.875rem" }}>Ex Works Price (per bottle): </strong>
                    <span style={{ fontSize: "0.875rem" }}>{item.price_ex_vat} EUR</span>
                  </div>
                )}
                {item.bottles_per_year && (
                  <div style={{ textAlign: "left" }}>
                    <strong style={{ fontSize: "0.875rem" }}>Bottles per Year: </strong>
                    <span style={{ fontSize: "0.875rem" }}>{item.bottles_per_year}</span>
                  </div>
                )}
                {item.container_size_cl && (
                  <div style={{ textAlign: "left" }}>
                    <strong style={{ fontSize: "0.875rem" }}>Container Size: </strong>
                    <span style={{ fontSize: "0.875rem" }}>{item.container_size_cl} cl</span>
                  </div>
                )}
                {item.abv_percentage && (
                  <div style={{ textAlign: "left" }}>
                    <strong style={{ fontSize: "0.875rem" }}>ABV: </strong>
                    <span style={{ fontSize: "0.875rem" }}>{item.abv_percentage} %</span>
                  </div>
                )}
              </Flex>

              </Flex>
            </Flex>
          </Card>
        ))}
      </Grid>


      {props.products.length <= 15 ? (
        <Grid 
          paddingBottom={"80px"}
          container item spacing={1}
        >
          <ItemButton elevation={0}>
              <Button
                onClick={handleAddProduct}
                style={{
                  backgroundColor: "black",
                  color: "white",
                  borderRadius: "0px",
                  puddingBottom: "80px",
                  width: "16em",
                }}
                variation="primary"
                colortheme="success"
              >
                Aggiungi Prodotto
              </Button>
          </ItemButton>
        </Grid>
      ) : (
        <br></br>
      )}


      <Dialog
        open={open}
        onClose={handleCloseDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
          Sei sicuro di voler eliminare questo prodotto? Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color="secondary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      </>

  );
}

export default ProductList;
